import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';

const { FiArrowRight, FiCpu, FiTarget, FiLayers, FiSearch, FiCheck, FiX } = FiIcons;

const Home = () => {
  const { t } = useLanguage();

  const frameworkSteps = [
    { title: t.home.engine.step1, desc: t.home.engine.step1Desc, icon: FiCpu },
    { title: t.home.engine.step2, desc: t.home.engine.step2Desc, icon: FiSearch },
    { title: t.home.engine.step3, desc: t.home.engine.step3Desc, icon: FiTarget },
    { title: t.home.engine.step4, desc: t.home.engine.step4Desc, icon: FiLayers },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-24 pb-20 lg:pt-32 lg:pb-32 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-[11px] uppercase tracking-[0.4em] text-brand-orange font-black mb-8"
            >
              {t.home.hero.tagline}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-6xl lg:text-8xl font-black text-brand-dark mb-10 tracking-tighter leading-tight italic"
            >
              {t.home.hero.headline}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl text-gray-500 mb-12 max-w-2xl mx-auto leading-relaxed font-medium italic"
            >
              {t.home.hero.subtext}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-6 justify-center"
            >
              <Link
                to="/ai-headhunter"
                className="bg-brand-orange text-white px-10 py-5 rounded-2xl text-lg font-black hover:bg-orange-600 transition-all shadow-2xl hover:-translate-y-1 flex items-center justify-center gap-3 uppercase tracking-widest"
              >
                {t.home.hero.primaryCta} <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
              </Link>
              <Link
                to="/mogelijkheden"
                className="bg-white text-brand-dark border-2 border-gray-100 px-10 py-5 rounded-2xl text-lg font-black hover:bg-gray-50 transition-all shadow-sm flex items-center justify-center uppercase tracking-widest"
              >
                {t.home.hero.secondaryCta}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Engine Section - Light Design */}
      <section className="py-32 bg-gray-50/50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-black text-brand-dark uppercase tracking-tighter italic mb-4">
              {t.home.engine.title}
            </h2>
            <p className="text-brand-orange text-[10px] font-black uppercase tracking-[0.5em]">{t.home.engine.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {frameworkSteps.map((step, idx) => (
              <div key={idx} className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-xl transition-all group">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-brand-orange transition-colors">
                  <SafeIcon icon={step.icon} className="w-6 h-6 text-brand-dark group-hover:text-white" />
                </div>
                <h3 className="text-lg font-black text-brand-dark mb-4 uppercase tracking-tight italic">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed italic font-medium">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Output Section - Branded but Light */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[3rem] p-12 lg:p-16 shadow-2xl border border-gray-100 relative overflow-hidden group">
             <div className="absolute top-0 right-0 p-12 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity">
                <SafeIcon icon={FiLayers} className="w-64 h-64" />
             </div>
             <h2 className="text-2xl font-black uppercase tracking-widest mb-12 text-brand-orange italic border-b border-gray-50 pb-8">
               {t.home.example.title}
             </h2>
             <div className="space-y-12">
                <div className="flex items-center gap-8">
                   <div className="text-7xl font-black text-brand-dark italic tracking-tighter">78%</div>
                   <div className="text-xs font-black text-gray-400 uppercase tracking-[0.3em] leading-tight">{t.home.example.score}</div>
                </div>
                <div className="grid md:grid-cols-2 gap-12">
                   <div>
                      <h4 className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-green-500 mb-6">
                         <div className="w-6 h-6 bg-green-50 rounded-lg flex items-center justify-center"><SafeIcon icon={FiCheck} /></div> {t.home.example.strong}
                      </h4>
                      <p className="text-sm font-bold italic text-gray-400 leading-relaxed px-2">Python, Data Analysis, SQL, Stakeholder Management</p>
                   </div>
                   <div>
                      <h4 className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-brand-orange mb-6">
                         <div className="w-6 h-6 bg-orange-50 rounded-lg flex items-center justify-center"><SafeIcon icon={FiX} /></div> {t.home.example.gaps}
                      </h4>
                      <p className="text-sm font-bold italic text-gray-400 leading-relaxed px-2">Cloud Architecture, Kubernetes, Advanced Security</p>
                   </div>
                </div>
                <div className="pt-10 border-t border-gray-50">
                   <p className="text-[10px] font-black uppercase tracking-widest text-gray-300 mb-4">{t.home.example.alt}</p>
                   <p className="text-xl font-black italic text-brand-dark uppercase tracking-tight">Data Analyst, Product Analyst, BI Specialist</p>
                </div>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;